<script setup lang="ts">
withDefaults(
  defineProps<{
    variant?: 'primary' | 'accent' | 'secondary' | 'ghost' | 'danger'
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    size?: 'sm' | 'md' | 'lg'
  }>(),
  {
    variant: 'primary',
    type: 'button',
    size: 'md',
  }
)
</script>

<template>
  <button
    :type="type"
    :disabled="disabled"
    class="btn"
    :class="[`btn--${variant}`, `btn--${size}`]"
  >
    <slot />
  </button>
</template>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  font-weight: var(--font-semibold);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    transform 0.05s ease;
}

.btn:focus-visible {
  outline: 2px solid var(--color-primary-light);
  outline-offset: 2px;
}

.btn--sm {
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-sm);
}
.btn--md {
  padding: var(--space-2) var(--space-4);
  font-size: var(--text-base);
}
.btn--lg {
  padding: var(--space-3) var(--space-6);
  font-size: var(--text-lg);
}

.btn--primary {
  background: linear-gradient(135deg, var(--color-primary-light), var(--color-primary));
  color: white;
  box-shadow: 0 4px 14px rgb(13 148 136 / 0.35);
}
.btn--primary:hover:not(:disabled) {
  background: linear-gradient(135deg, var(--color-primary), var(--color-primary-dark));
  box-shadow: 0 6px 18px rgb(13 148 136 / 0.45);
  transform: translateY(-1px);
}

.btn--accent {
  background: linear-gradient(135deg, var(--color-accent-light), var(--color-accent));
  color: var(--color-text);
  box-shadow: 0 4px 14px rgb(217 119 6 / 0.35);
}
.btn--accent:hover:not(:disabled) {
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-dark));
  color: white;
  box-shadow: 0 6px 18px rgb(217 119 6 / 0.45);
  transform: translateY(-1px);
}

.btn--secondary {
  background-color: var(--color-surface);
  color: var(--color-primary);
  border-color: var(--color-border-strong);
}
.btn--secondary:hover:not(:disabled) {
  background-color: var(--color-surface-raised);
}

.btn--ghost {
  background-color: transparent;
  color: var(--color-text);
}
.btn--ghost:hover:not(:disabled) {
  background-color: var(--color-surface-raised);
}

.btn--danger {
  background-color: var(--color-error);
  color: white;
}
.btn--danger:hover:not(:disabled) {
  filter: brightness(0.95);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
